/**
 * Pure read/view helpers for the hosted web's single-project Agents subset
 * (Runs rail). No DOM, no invoke — app.ts wires polling and actions.
 */
import type {
  AgentControlDetailView,
  AgentControlRowView,
  AgentJobStatus,
  AgentSandboxView,
  AgentTimelineItemView,
  AgentTimelinePayloadView,
} from "@tavsin/desktop/channels";

export const AGENTS_LIVE_POLL_MS = 2_500;
export const AGENTS_IDLE_POLL_MS = 15_000;

export interface AgentRowGroups {
  running: AgentControlRowView[];
  queued: AgentControlRowView[];
  recent: AgentControlRowView[];
}

/** Running + queued rows — drives fast polling. */
export function agentsListLive(rows: readonly AgentControlRowView[]): boolean {
  return rows.some((r) => r.status === "running" || r.status === "queued");
}

export function agentsPollIntervalMs(live: boolean): number {
  return live ? AGENTS_LIVE_POLL_MS : AGENTS_IDLE_POLL_MS;
}

export function groupAgentRows(rows: readonly AgentControlRowView[]): AgentRowGroups {
  const running: AgentControlRowView[] = [];
  const queued: AgentControlRowView[] = [];
  const recent: AgentControlRowView[] = [];
  for (const row of rows) {
    if (row.status === "running") running.push(row);
    else if (row.status === "queued") queued.push(row);
    else recent.push(row);
  }
  return { running, queued, recent };
}

/** Merge incremental timeline pages by seq so overlapping polls cannot duplicate rows. */
export function mergeTimelineItems(
  current: readonly AgentTimelineItemView[],
  incoming: readonly AgentTimelineItemView[],
): AgentTimelineItemView[] {
  const bySeq = new Map<number, AgentTimelineItemView>();
  for (const item of current) bySeq.set(item.seq, item);
  for (const item of incoming) bySeq.set(item.seq, item);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/**
 * Never render a raw absolute path. Commands and relative paths pass through;
 * `/Users/…`, `/repo/…`, `C:\…` collapse to the last two segments.
 */
export function safeDisplayText(text: string): string {
  return text.replace(/(?:[A-Za-z]:)?(?:\/|\\)(?:[^\s"'<>\\]+(?:\/|\\))+[^\s"'<>\\]*/g, (match) => {
    const parts = match.split(/[/\\]/).filter((p) => p.length > 0 && !p.endsWith(":"));
    const tail = parts.slice(-2).join("/");
    return tail || "path";
  });
}

/** Branch-only sandboxes (`present: false` after worktree eviction) still have a reviewable branch. */
export function sandboxLifecycleAvailable(sandbox: AgentSandboxView | null | undefined): boolean {
  if (!sandbox) return false;
  return sandbox.present === true || Boolean(sandbox.branch);
}

export interface WebAgentActionGates {
  readonly canSteer: boolean;
  readonly canCancel: boolean;
  readonly canSandbox: boolean;
}

export function agentRowLive(row: AgentControlRowView): boolean {
  return row.status === "running" || row.status === "queued";
}

/**
 * Action affordances from the current list row + sandbox snapshot only.
 * Settled jobs never steer, even if steerable is stale true on the row.
 */
export function webAgentActionGates(input: {
  row: AgentControlRowView | null;
  sandbox: AgentSandboxView | null | undefined;
}): WebAgentActionGates {
  if (!input.row) {
    return { canSteer: false, canCancel: false, canSandbox: false };
  }
  const live = agentRowLive(input.row);
  return {
    canSteer: live && input.row.steerable === true,
    canCancel: live,
    canSandbox: sandboxLifecycleAvailable(input.sandbox),
  };
}

/** Reload agents.detail when the list row's status or event count moves. */
export function shouldReloadAgentDetail(
  synced: { readonly status: AgentJobStatus; readonly eventCount: number } | null,
  row: AgentControlRowView,
): boolean {
  if (!synced) return true;
  return synced.status !== row.status || synced.eventCount !== row.eventCount;
}

export function statusLabel(status: AgentJobStatus): string {
  return status;
}

export function costLabel(usd: number): string {
  if (!Number.isFinite(usd) || usd <= 0) return "$0.00";
  return `$${usd.toFixed(2)}`;
}

export function modelLabel(row: AgentControlRowView): string | null {
  if (!row.model) return null;
  return `${row.model.provider} · ${row.model.model}`;
}

export function payloadPreview(payload: AgentTimelinePayloadView): string {
  switch (payload.kind) {
    case "command":
      return safeDisplayText(payload.command);
    case "file":
      return `${payload.action} ${safeDisplayText(payload.path)}`;
    case "agent":
      return `${payload.phase} ${payload.agent}`;
    case "test":
      return safeDisplayText(payload.command);
    case "browser":
      return payload.url ? safeDisplayText(`${payload.action} ${payload.url}`) : payload.action;
    case "error":
      return safeDisplayText(payload.message);
    case "gate":
      return safeDisplayText(payload.text);
    case "steer":
    case "tool":
    case "note":
      return safeDisplayText(payload.text);
    default: {
      const _x: never = payload;
      return String(_x);
    }
  }
}

export function rowFromDetail(detail: AgentControlDetailView): AgentControlRowView {
  const { timeline: _t, verification: _v, ...row } = detail;
  return row;
}
